import React, { useState } from 'react';
import { ArrowRight, ArrowLeft, Sparkles, Moon, Utensils, Clock3, Check, Gift, ShieldCheck, XCircle } from 'lucide-react';
import './OnboardingPage.css';
import { apiGet, apiPost } from '../../lib/api';
import SetupExperience from '../../components/SetupExperience';

const STEP_META = {
  1: { icon: Sparkles, eyebrow: 'Step 1 of 4',  title: 'Welcome to Hourtrix',          sub: 'Let\'s set the gold standard for your day.' },
  2: { icon: Moon,     eyebrow: 'Step 2 of 4',  title: 'How much sleep do you protect?', sub: 'We\'ll carve out the rest of your day around this.' },
  3: { icon: Utensils, eyebrow: 'Step 3 of 4',  title: 'How long do you eat each day?',  sub: 'A realistic daily estimate — breakfast, lunch, and dinner combined.' },
  4: { icon: Clock3,   eyebrow: 'Step 4 of 4',  title: 'Daily breaks & transitions',     sub: 'Tiny buffers add up. Give yourself a few honest minutes.' },
};

function OnboardingPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [showSetupExperience, setShowSetupExperience] = useState(false);
  const [saveReady, setSaveReady] = useState(false);

  const [formData, setFormData] = useState({
    display_name: '',
    coach_name: '',
    sleep_hours: 8,
    eating_minutes: 60,
    toilet_minutes: 15
  });

  const handleNext = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentStep((s) => s + 1);
      setIsTransitioning(false);
    }, 300);
  };

  const handleBack = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentStep((s) => s - 1);
      setIsTransitioning(false);
    }, 300);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.display_name.trim() || formData.display_name.trim().length > 40) {
      errors.display_name = 'Name must be 1–40 characters';
    }
    const coachName = formData.coach_name.trim();
    if (!coachName || coachName.length > 20) {
      errors.coach_name = 'Coach name must be 1–20 characters';
    } else if (!/^[a-zA-Z0-9\s]+$/.test(coachName)) {
      errors.coach_name = 'Only letters, numbers, and spaces allowed';
    }
    if (![6, 7, 8, 9, 10].includes(formData.sleep_hours)) {
      errors.sleep_hours = 'Please select sleep hours';
    }
    if (formData.eating_minutes < 0 || formData.eating_minutes > 300) {
      errors.eating_minutes = 'Must be 0–300 minutes';
    }
    if (formData.toilet_minutes < 0 || formData.toilet_minutes > 180) {
      errors.toilet_minutes = 'Must be 0–180 minutes';
    }
    return errors;
  };

  const handleSubmit = async () => {
    if (isSaving) return; // double-submit guard
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setSaveError('Please fix the errors above');
      return;
    }
    setValidationErrors({});
    setSaveError(null);
    setIsSaving(true);

    // Show the premium setup experience IMMEDIATELY — it guarantees a
    // minimum 7-second premium loader while we save + poll /me in the background.
    setShowSetupExperience(true);
    setSaveReady(false);

    try {
      const response = await apiPost('/api/user/onboarding/save', formData);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData.detail?.errors?.[0] || 'Failed to save';
        throw new Error(errorMsg);
      }

      let userData = null;
      const POLL_DELAYS = [300, 600, 1200, 2000, 3000];
      let retries = POLL_DELAYS.length;
      let attempt = 0;
      while (retries > 0) {
        const meResponse = await apiGet('/api/auth/me');
        if (meResponse.ok) {
          userData = await meResponse.json();
          if (userData.is_new_user === false && userData.onboarding_completed === true) break;
        }
        retries--;
        if (retries > 0) await new Promise((r) => setTimeout(r, POLL_DELAYS[attempt]));
        attempt++;
      }

      if (!userData || userData.is_new_user === true || userData.onboarding_completed === false) {
        throw new Error('Saved, but account update pending. Please tap Finish again.');
      }
      // Signal the SetupExperience that backend work is done — it will hold the
      // animation to the minimum 7s and then fire onComplete.
      setSaveReady(true);
    } catch (err) {
      // Hide the setup experience so user can retry
      setShowSetupExperience(false);
      setSaveError(err.message || "Couldn't save your onboarding details. Please try again.");
      setIsSaving(false);
    }
  };

  const handleSetupComplete = () => {
    window.location.replace('/app');
  };

  const NAME_REGEX = /^[a-zA-Z0-9 ]+$/;
  const canProceedStep1 = NAME_REGEX.test(formData.display_name.trim()) &&
                          NAME_REGEX.test(formData.coach_name.trim());
  const StepIcon = STEP_META[currentStep].icon;

  // Premium 7s post-onboarding experience takes over the screen until setup finishes.
  if (showSetupExperience) {
    return (
      <SetupExperience
        ready={saveReady}
        onComplete={handleSetupComplete}
        coachName={formData.coach_name}
      />
    );
  }

  return (
    <div className="ob-root" data-testid="onboarding-page">
      <div className="ob-ambient" aria-hidden="true">
        <div className="ob-glow ob-glow-a" />
        <div className="ob-glow ob-glow-b" />
      </div>

      <div className="ob-shell">
        <header className="ob-brand">
          <span className="ob-brand-dot" />
          <span className="ob-brand-word">Hourtrix</span>
        </header>

        {/* === Premium 7-day free trial banner === */}
        {currentStep === 1 ? (
          <div className="ob-trial-hero" data-testid="trial-hero">
            <div className="ob-trial-hero-badge">
              <Gift size={13} strokeWidth={2.4} />
              <span>Start your free 7-day trial</span>
            </div>
            <h2 className="ob-trial-hero-title">
              Try every feature for <span className="ob-trial-hero-accent">7 days, on the house</span>.
            </h2>
            <ul className="ob-trial-bullets" role="list">
              <li>
                <ShieldCheck size={14} strokeWidth={2.4} />
                <span><strong>No card required</strong> to begin — setup takes under a minute.</span>
              </li>
              <li>
                <XCircle size={14} strokeWidth={2.4} />
                <span>Cancel anytime during your trial. Zero charges, zero friction.</span>
              </li>
              <li>
                <Sparkles size={14} strokeWidth={2.4} />
                <span>After 7 days, your trial simply ends. <strong>Continue only if you love it.</strong></span>
              </li>
            </ul>
          </div>
        ) : (
          <div className="ob-trial-chip" data-testid="trial-chip">
            <Gift size={12} strokeWidth={2.4} />
            <span>7-day free trial · no card required · cancel anytime</span>
          </div>
        )}

        <div className="ob-card">
          {/* Progress */}
          <div className="ob-progress">
            {[1, 2, 3, 4].map((n) => (
              <div
                key={n}
                className={`ob-progress-pip ${n <= currentStep ? 'is-filled' : ''} ${n === currentStep ? 'is-current' : ''}`}
              />
            ))}
          </div>

          <div className="ob-eyebrow" data-testid="step-eyebrow">
            <StepIcon size={14} strokeWidth={2.2} />
            <span>{STEP_META[currentStep].eyebrow}</span>
          </div>

          <div className={`ob-step ${isTransitioning ? 'is-exit' : 'is-enter'}`}>
            <h1 className="ob-title">{STEP_META[currentStep].title}</h1>
            <p className="ob-sub">{STEP_META[currentStep].sub}</p>

            {/* Step 1 — Names */}
            {currentStep === 1 && (
              <div className="ob-fields" data-testid="onboarding-step-1">
                <label className="ob-field">
                  <span className="ob-label">Your name</span>
                  <input
                    id="display_name"
                    type="text"
                    value={formData.display_name}
                    onChange={(e) => {
                      setFormData({ ...formData, display_name: e.target.value });
                      setValidationErrors({ ...validationErrors, display_name: null });
                    }}
                    placeholder="Jane"
                    maxLength={40}
                    data-testid="input-display-name"
                    className={`ob-input ${validationErrors.display_name ? 'is-invalid' : ''}`}
                  />
                  {validationErrors.display_name && (
                    <span className="ob-err">{validationErrors.display_name}</span>
                  )}
                </label>

                <label className="ob-field">
                  <span className="ob-label">Name your AI coach</span>
                  <input
                    id="coach_name"
                    type="text"
                    value={formData.coach_name}
                    onChange={(e) => {
                      setFormData({ ...formData, coach_name: e.target.value });
                      setValidationErrors({ ...validationErrors, coach_name: null });
                    }}
                    placeholder="Hour"
                    maxLength={20}
                    data-testid="input-coach-name"
                    className={`ob-input ${validationErrors.coach_name ? 'is-invalid' : ''}`}
                  />
                  <span className="ob-hint">This is how your coach will introduce itself in chat.</span>
                  {validationErrors.coach_name && (
                    <span className="ob-err">{validationErrors.coach_name}</span>
                  )}
                </label>
              </div>
            )}

            {/* Step 2 — Sleep */}
            {currentStep === 2 && (
              <div className="ob-sleep" data-testid="onboarding-step-2">
                {[6, 7, 8, 9, 10].map((hours) => (
                  <button
                    key={hours}
                    type="button"
                    className={`ob-sleep-pill ${formData.sleep_hours === hours ? 'is-selected' : ''}`}
                    onClick={() => setFormData({ ...formData, sleep_hours: hours })}
                    data-testid={`btn-sleep-${hours}`}
                  >
                    <span className="ob-sleep-num">{hours}</span>
                    <span className="ob-sleep-unit">hours</span>
                    {formData.sleep_hours === hours && (
                      <span className="ob-sleep-check"><Check size={12} strokeWidth={3} /></span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Step 3 — Eating */}
            {currentStep === 3 && (
              <div className="ob-fields" data-testid="onboarding-step-3">
                <label className="ob-field">
                  <span className="ob-label">Minutes per day</span>
                  <div className="ob-number-row">
                    <button
                      type="button"
                      className="ob-stepper"
                      onClick={() => setFormData({ ...formData, eating_minutes: Math.max(0, formData.eating_minutes - 5) })}
                      data-testid="btn-eat-minus"
                      aria-label="Decrease"
                    >−</button>
                    <input
                      id="eating_minutes"
                      type="number"
                      min="0"
                      max="300"
                      value={formData.eating_minutes}
                      onChange={(e) => { const val = parseInt(e.target.value, 10); setFormData({ ...formData, eating_minutes: Number.isFinite(val) ? val : 0 }); }}
                      data-testid="input-eating-minutes"
                      className="ob-input ob-input-number"
                    />
                    <button
                      type="button"
                      className="ob-stepper"
                      onClick={() => setFormData({ ...formData, eating_minutes: Math.min(300, formData.eating_minutes + 5) })}
                      data-testid="btn-eat-plus"
                      aria-label="Increase"
                    >+</button>
                  </div>
                  <span className="ob-hint">Suggested: 60 min/day (~15 min per meal + buffer).</span>
                </label>
              </div>
            )}

            {/* Step 4 — Toilet / breaks */}
            {currentStep === 4 && (
              <div className="ob-fields" data-testid="onboarding-step-4">
                <label className="ob-field">
                  <span className="ob-label">Minutes per day</span>
                  <div className="ob-number-row">
                    <button
                      type="button"
                      className="ob-stepper"
                      onClick={() => setFormData({ ...formData, toilet_minutes: Math.max(0, formData.toilet_minutes - 5) })}
                      data-testid="btn-toilet-minus"
                      aria-label="Decrease"
                    >−</button>
                    <input
                      id="toilet_minutes"
                      type="number"
                      min="0"
                      max="180"
                      value={formData.toilet_minutes}
                      onChange={(e) => { const val = parseInt(e.target.value, 10); setFormData({ ...formData, toilet_minutes: Number.isFinite(val) ? val : 0 }); }}
                      data-testid="input-toilet-minutes"
                      className="ob-input ob-input-number"
                    />
                    <button
                      type="button"
                      className="ob-stepper"
                      onClick={() => setFormData({ ...formData, toilet_minutes: Math.min(180, formData.toilet_minutes + 5) })}
                      data-testid="btn-toilet-plus"
                      aria-label="Increase"
                    >+</button>
                  </div>
                  <span className="ob-hint">Suggested: 10–20 min/day.</span>
                </label>

                {saveError && (
                  <div className="ob-save-error" data-testid="save-error">
                    <p>{saveError}</p>
                    <button
                      onClick={() => { setSaveError(null); handleSubmit(); }}
                      className="ob-retry"
                      data-testid="btn-retry"
                      disabled={isSaving}
                    >
                      Try again
                    </button>
                  </div>
                )}
              </div>
            )}

            <p className="ob-note">You can change any of this later in Settings.</p>
          </div>

          {/* Actions */}
          <div className="ob-actions">
            {currentStep > 1 ? (
              <button
                onClick={handleBack}
                className="ob-btn ob-btn-ghost"
                disabled={isSaving}
                data-testid="btn-back"
              >
                <ArrowLeft size={16} strokeWidth={2.4} />
                <span>Back</span>
              </button>
            ) : <div />}

            {currentStep < 4 && (
              <button
                onClick={handleNext}
                disabled={(currentStep === 1 && !canProceedStep1) || isSaving || isTransitioning}
                className="ob-btn ob-btn-primary"
                data-testid={`btn-next-step-${currentStep}`}
              >
                <span>Continue</span>
                <ArrowRight size={16} strokeWidth={2.4} />
              </button>
            )}

            {currentStep === 4 && (
              <button
                onClick={handleSubmit}
                disabled={isSaving}
                className="ob-btn ob-btn-primary"
                data-testid="btn-finish"
              >
                <span>{isSaving ? 'Saving…' : 'Finish setup'}</span>
                {!isSaving && <ArrowRight size={16} strokeWidth={2.4} />}
              </button>
            )}
          </div>
        </div>

        <p className="ob-footer">Hourtrix · Spend your hours like they are gold.</p>
      </div>
    </div>
  );
}

export default OnboardingPage;
